import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  AutocompleteOption,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Table,
  Textarea,
  Tooltip,
  Typography,
} from '@mui/joy';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaChevronLeft, FaChevronRight, FaDownload, FaEdit, FaPlus, FaTrash, FaUserPlus } from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';
import CustomerCreationForm from '../components/CustomerCreationForm';
import { deleteProject } from '../hooks/useProjects';
import {
  DEFAULT_WORKSPACE_PAGE_SIZE,
  WORKSPACE_PAGE_SIZE_OPTIONS,
  WORKSPACE_STAGE_FILTERS,
  useWorkspacesList,
  type WorkspaceStatusFilter,
} from '../hooks/useWorkspacesList';
import { useCustomerStore } from '../store/customerStore';
import type { Customer } from '../services/customerService';
import type { Project } from '../types/project';
import type { WorkspaceSnapshot } from './project-workspace/workspaceDashboard.model';
import { InstallsAtRiskCard, PendingClosureCard, ReadyToBookCard } from './project-workspace/WorkspaceDashboardGates';
import { stateShortLabel, workspaceStageLabel, type WorkspaceStage } from './project-workspace/workspaceStages';
import { getFullWorkflowTasksForMode, workspaceLifecycleStatus } from '../utils/workflowUtils';
import { directSupabase } from '../lib/supabaseClient';
import {
  DASHBOARD_VIEWS,
  WORKSPACE_OPS_VIEW_KEY,
  boardCardsByAcceptance,
  emptyWorkspaceOperations,
  isDashboardView,
  type DashboardView,
  type WorkspaceOperations,
} from './project-workspace/workspaceDashboard.model';
import type { OperationalFilter } from './project-workspace/operationalState';
import { loadWorkspaceOperations } from './project-workspace/workspaceDashboard.service';
import WorkspaceDashboardToday from './project-workspace/WorkspaceDashboardToday';
import WorkspaceDashboardBoard from './project-workspace/WorkspaceDashboardBoard';
import WorkspaceDashboardWeek from './project-workspace/WorkspaceDashboardWeek';
import WorkspaceDashboardJobs from './project-workspace/WorkspaceDashboardJobs';
import {
  WorkspaceDashboardScheduled,
  WorkspaceDashboardUnscheduled,
} from './project-workspace/WorkspaceDashboardSchedule';
import {
  emitProjectCreatedActivity,
  withProjectCreator,
} from '../services/activityEvents.service';
import { mintWorkspaceLifecycleRef } from './project-workspace/workspaceLifecycleRef';
import { brandForCustomer, saveWorkspaceBrand } from './project-workspace/workspaceBrand';
import { useBrands } from '../hooks/useBrands';
import { selectableBrands } from '../services/organisationSettings';
import { defaultUseBillingDetailsForDocuments } from './project-workspace/workspaceBillingIdentity';
import { assertJobRecordAllowsComplete } from '../services/jobReviews.service';
import {
  jobListExportRows,
  jobListXls,
  pickLifecycleRefByProject,
  pickSurveyVersionByProject,
  surveyVersionGrossTotal,
  workspaceDashboardXls,
  type LinkedLifecycleDoc,
  type SurveyVersionHeader,
  type SurveyVersionLine,
  type WorkspaceSurveyRow,
} from './workspacesDashboardExport';

type EditorMode = 'create' | 'edit';

const STAGE_COLOR: Record<WorkspaceStage, 'neutral' | 'primary' | 'warning' | 'danger' | 'success'> = {
  presale: 'neutral',
  deposit: 'warning',
  ordering: 'primary',
  installation: 'primary',
  aftercare: 'success',
  completed: 'success',
  cancelled: 'neutral',
};

/** Stage chip with the state under it: the same answer as the Board. */
function StageCell({ snapshot }: { snapshot: WorkspaceSnapshot | null }) {
  if (!snapshot) return <Typography level="body-xs" sx={{ color: 'neutral.400' }}>…</Typography>;
  const detail = snapshot.state ? stateShortLabel(snapshot.state) : '';
  return (
    <Stack gap={0.25} alignItems="flex-start">
      <Chip size="sm" color={STAGE_COLOR[snapshot.stage]} variant="soft">
        {workspaceStageLabel(snapshot.stage)}
      </Chip>
      {detail && (
        <Typography level="body-xs" sx={{ color: snapshot.exception ? 'danger.600' : 'neutral.500', lineHeight: 1.25 }}>
          {detail}
        </Typography>
      )}
    </Stack>
  );
}

function stageText(snapshot: WorkspaceSnapshot | null): string {
  if (!snapshot) return '';
  const detail = snapshot.state ? stateShortLabel(snapshot.state) : '';
  return detail ? `${workspaceStageLabel(snapshot.stage)} · ${detail}` : workspaceStageLabel(snapshot.stage);
}

function lifecycleTimestampPatch(
  previous: 'active' | 'completed' | 'cancelled' | null,
  next: 'active' | 'completed' | 'cancelled',
  existing?: Project | null
) {
  const nowIso = new Date().toISOString();
  const statusChanged = previous == null || previous !== next;
  const patch: { status_changed_at?: string; completed_at?: string | null; cancelled_at?: string | null } = {};
  if (statusChanged) patch.status_changed_at = nowIso;
  if (next === 'completed') {
    patch.completed_at = statusChanged || !existing?.completed_at ? nowIso : existing.completed_at;
    patch.cancelled_at = null;
    return patch;
  }
  if (next === 'cancelled') {
    patch.cancelled_at = statusChanged || !existing?.cancelled_at ? nowIso : existing.cancelled_at;
    patch.completed_at = null;
    return patch;
  }
  patch.completed_at = null;
  patch.cancelled_at = null;
  return patch;
}

type WorkspaceDocLinks = {
  refs: Record<string, { id: string; ref: string }>;
  totals: Record<string, number>;
};

async function selectInChunks<T>(
  table: string,
  columns: string,
  column: string,
  values: string[],
  refine: (query: any) => any = (query) => query,
): Promise<T[]> {
  const rows: T[] = [];
  for (let index = 0; index < values.length; index += 200) {
    const chunk = values.slice(index, index + 200);
    const { data, error } = await refine(directSupabase.from(table).select(columns).in(column, chunk));
    if (error) throw new Error(error.message);
    rows.push(...((data || []) as T[]));
  }
  return rows;
}

async function loadSurveyTotals(projectIds: string[]): Promise<Record<string, number>> {
  const surveys = await selectInChunks<WorkspaceSurveyRow>(
    'visual_designer_surveys',
    'project_id, kind, option_id, document_version_id',
    'project_id',
    projectIds,
    (query) => query.in('kind', ['first', 'final']),
  );
  const versionByProject = pickSurveyVersionByProject(surveys);
  const versionIds = [...new Set(Object.values(versionByProject))];
  if (!versionIds.length) return {};
  const [headers, lines] = await Promise.all([
    selectInChunks<SurveyVersionHeader>(
      'document_versions',
      'id, admin_pct, admin_cap, contingency_pct, profit_pct, vat_enabled, vat_pct',
      'id',
      versionIds,
    ),
    selectInChunks<SurveyVersionLine>(
      'document_version_lines',
      'version_id, line_kind, qty, unit_price, line_total, vat_inclusive',
      'version_id',
      versionIds,
    ),
  ]);
  const headerById = new Map(headers.map((header) => [header.id, header]));
  const totals: Record<string, number> = {};
  for (const [projectId, versionId] of Object.entries(versionByProject)) {
    const header = headerById.get(versionId);
    if (!header) continue;
    totals[projectId] = surveyVersionGrossTotal(header, lines.filter((line) => line.version_id === versionId));
  }
  return totals;
}

async function loadWorkspaceDocLinks(projectIds: string[]): Promise<WorkspaceDocLinks> {
  const ids = [...new Set(projectIds.filter(Boolean))];
  if (!ids.length) return { refs: {}, totals: {} };
  const [docs, totals] = await Promise.all([
    selectInChunks<LinkedLifecycleDoc>(
      'documents',
      'id, project_id, lifecycle_ref, kind, created_at',
      'project_id',
      ids,
      (query) => query.in('kind', ['Quotation', 'Estimate', 'Invoice']).neq('status', 'void'),
    ),
    loadSurveyTotals(ids),
  ]);
  return { refs: pickLifecycleRefByProject(docs), totals };
}

const GBP = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

function formatProjectTotal(totals: Record<string, number>, projectId: string) {
  return projectId in totals ? GBP.format(totals[projectId]) : '—';
}

function downloadXls(filename: string, xml: string) {
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function LifecycleRefLink({
  projectId,
  workspaceRef,
  refs,
}: {
  projectId: string;
  workspaceRef?: string | null;
  refs: Record<string, { id: string; ref: string }>;
}) {
  const doc = refs[projectId];
  const ref = String(workspaceRef || '').trim() || doc?.ref || '';
  if (!ref) {
    return (
      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
        —
      </Typography>
    );
  }
  return (
    <Typography
      level="body-sm"
      component={Link}
      to={`/projects/${projectId}/workspace`}
      sx={{ textDecoration: 'none', fontWeight: 'lg' }}
    >
      {ref}
    </Typography>
  );
}

function matchCustomer(customers: Customer[], project: Project): Customer | null {
  return (
    customers.find((item) => item.id?.toString() === project.customer_id?.toString()) ||
    customers.find((item) => item.email && item.email === project.customer_email) ||
    null
  );
}

const emptyForm = {
  project_name: '',
  customer: null as Customer | null,
  notes: '',
  supply_only: false,
  project_status: 'active' as 'active' | 'completed' | 'cancelled',
  /** Brand chosen for a new workspace. Empty means: from the customer. */
  organisation_id: '',
};

const WorkspacesDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isList = location.pathname === '/workspaces/list' || location.pathname.startsWith('/workspaces/list/');
  const { customers, loading: customersLoading, loadCustomers, addCustomer } = useCustomerStore();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WorkspaceStatusFilter>('all');
  const [stageFilter, setStageFilter] = useState<'all' | WorkspaceStage>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_WORKSPACE_PAGE_SIZE);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>('create');
  const [editing, setEditing] = useState<Project | null>(null);
  const [form, setForm] = useState(emptyForm);
  const { brands } = useBrands();
  const suggestedBrand = useMemo(() => brandForCustomer(brands, form.customer as any), [brands, form.customer]);
  const chosenBrandId = form.organisation_id || suggestedBrand?.id || '';
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lifecycleRefs, setLifecycleRefs] = useState<Record<string, { id: string; ref: string }>>({});
  const [projectTotals, setProjectTotals] = useState<Record<string, number>>({});
  const [view, setView] = useState<DashboardView>(() => {
    try {
      const stored = sessionStorage.getItem(WORKSPACE_OPS_VIEW_KEY);
      return isDashboardView(stored) ? stored : 'today';
    } catch {
      return 'today';
    }
  });
  const [opsFilter, setOpsFilter] = useState<OperationalFilter>({ kind: 'all' });
  const [ops, setOps] = useState<WorkspaceOperations>(emptyWorkspaceOperations);
  const [opsLoading, setOpsLoading] = useState(true);
  const [opsError, setOpsError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    try {
      sessionStorage.setItem(WORKSPACE_OPS_VIEW_KEY, view);
    } catch {
      // Ignore storage write failures.
    }
  }, [view]);

  useEffect(() => {
    if (isList) {
      setOpsLoading(false);
      return;
    }
    let cancelled = false;
    setOpsLoading(true);
    setOpsError(null);
    void loadWorkspaceOperations()
      .then((next) => {
        if (!cancelled) setOps(next);
      })
      .catch((err) => {
        if (!cancelled) setOpsError(err instanceof Error ? err.message : 'Failed to load workspace briefing');
      })
      .finally(() => {
        if (!cancelled) setOpsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isList]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, stageFilter]);

  const reloadOps = useCallback(async () => {
    try {
      setOps(await loadWorkspaceOperations());
    } catch (err) {
      setOpsError(err instanceof Error ? err.message : 'Failed to load workspace briefing');
    }
  }, []);

  const { projects, total, loading, error, refresh, listMatching, snapshotFor } = useWorkspacesList({
    search: debouncedSearch,
    statusFilter,
    stageFilter,
    page,
    pageSize,
    enabled: isList,
  });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (editorOpen && customers.length === 0) loadCustomers();
  }, [editorOpen, customers.length, loadCustomers]);

  useEffect(() => {
    const ids = projects.map((project) => project.id);
    if (!ids.length) {
      setLifecycleRefs({});
      setProjectTotals({});
      return;
    }
    let cancelled = false;
    void loadWorkspaceDocLinks(ids)
      .then((next) => {
        if (cancelled) return;
        setLifecycleRefs(next.refs);
        setProjectTotals(next.totals);
      })
      .catch(() => {
        if (cancelled) return;
        setLifecycleRefs({});
        setProjectTotals({});
      });
    return () => {
      cancelled = true;
    };
  }, [projects]);

  useEffect(() => {
    if (!editorOpen || !editing || form.customer || customers.length === 0) return;
    const customer = matchCustomer(customers, editing);
    if (customer) setForm((prev) => ({ ...prev, customer }));
  }, [customers, editorOpen, editing, form.customer]);

  const openCreate = () => {
    setEditorMode('create');
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setAddingCustomer(false);
    setEditorOpen(true);
  };

  const openEdit = (project: Project) => {
    const customer = matchCustomer(customers, project);
    setEditorMode('edit');
    setEditing(project);
    setForm({
      project_name: project.project_name || '',
      customer,
      notes: project.notes || '',
      supply_only: !!project.supply_only,
      project_status: project.project_status || 'active',
      organisation_id: '',
    });
    setFormError(null);
    setAddingCustomer(false);
    setEditorOpen(true);
  };

  const applyCustomer = (customer: Customer | null) => {
    setForm((prev) => ({ ...prev, customer }));
  };

  const discardEditor = () => {
    setEditorOpen(false);
    setAddingCustomer(false);
    setFormError(null);
  };

  const persistWorkspace = async () => {
    if (!form.project_name.trim()) {
      setFormError('Workspace name is required');
      return;
    }
    if (!form.customer) {
      setFormError('Customer is required');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const customer = form.customer;
      if (editorMode === 'create') {
        const workflow_tasks = getFullWorkflowTasksForMode([], form.supply_only);
        const lifecycle_ref = await mintWorkspaceLifecycleRef(chosenBrandId || null);
        const insertPayload = await withProjectCreator({
            project_name: form.project_name.trim(),
            customer_id: customer.id,
            customer_name: customer.name,
            customer_email: customer.email || '',
            notes: form.notes || '',
            deposit_received: false,
            ordered: false,
            delivery_date: null,
            product_types: [],
            supplier_quote_received: false,
            supplier_prices: [],
            complete: false,
            workflow_tasks,
            status: 'pending',
            preferred_suppliers: [],
            onedrive_folder_id: null,
            supply_only: form.supply_only,
            project_status: 'active',
            lifecycle_ref,
            use_billing_details_for_documents: defaultUseBillingDetailsForDocuments(customer),
          });
        const { data, error: insertError } = await directSupabase
          .from('projects')
          .insert([insertPayload])
          .select('id')
          .single();
        if (insertError) throw new Error(insertError.message);
        if (data?.id && chosenBrandId) await saveWorkspaceBrand(data.id, chosenBrandId);
        if (data?.id) {
          emitProjectCreatedActivity({
            projectId: data.id,
            customerId: customer.id,
            customerName: customer.name,
            actorId: (insertPayload as { created_by?: string }).created_by || null,
          });
        }
        setEditorOpen(false);
        if (data?.id) navigate(`/projects/${data.id}/workspace`);
        return;
      }
      if (!editing) return;
      const previousStatus = editing.project_status || 'active';
      if (form.project_status === 'completed' && previousStatus !== 'completed') {
        await assertJobRecordAllowsComplete(editing.id);
      }
      const workflow_tasks =
        editing.supply_only === form.supply_only
          ? editing.workflow_tasks
          : getFullWorkflowTasksForMode(editing.workflow_tasks || [], form.supply_only, editing);
      const { error: updateError } = await directSupabase
        .from('projects')
        .update({
          project_name: form.project_name.trim(),
          customer_id: customer.id,
          customer_name: customer.name,
          customer_email: customer.email || '',
          notes: form.notes || '',
          supply_only: form.supply_only,
          project_status: form.project_status,
          complete: form.project_status === 'completed',
          workflow_tasks,
          ...lifecycleTimestampPatch(previousStatus, form.project_status, editing),
        })
        .eq('id', editing.id);
      if (updateError) throw new Error(updateError.message);
      setEditorOpen(false);
      await refresh();
      await reloadOps();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save workspace');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError(null);
    try {
      await deleteProject(deleteTarget.id);
      setDeleteTarget(null);
      await refresh();
      await reloadOps();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete workspace');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const exportCurrentView = useCallback(async () => {
    setExporting(true);
    setActionError(null);
    try {
      // Live Job / Pre-Sale export the tab as shown; other views export the workspace list.
      if (!isList && (view === 'live' || view === 'presale')) {
        const accepted = view === 'live';
        const label = accepted ? 'Live Job' : 'Pre-Sale';
        const rows = jobListExportRows(boardCardsByAcceptance(ops, accepted), window.location.origin);
        downloadXls(
          `workspaces-${accepted ? 'live-job' : 'pre-sale'}-${new Date().toISOString().slice(0, 10)}.xls`,
          jobListXls(label, rows, accepted),
        );
        return;
      }
      const { rows, snapshots } = await listMatching();
      const { refs } = await loadWorkspaceDocLinks(rows.map((project) => project.id));
      const origin = window.location.origin;
      const xls = workspaceDashboardXls(
        rows.map((project) => {
          const linked = refs[project.id];
          return {
            workspace: project.project_name || 'Untitled',
            workspaceHref: `${origin}/projects/${project.id}/workspace`,
            customer: project.customer_name || '',
            email: project.customer_email || '',
            lifecycle: stageText(snapshots[project.id] || null),
            lifecycleRef: String(project.lifecycle_ref || '').trim() || linked?.ref || '',
            lifecycleRefHref: linked ? `${origin}/docs/${linked.id}` : undefined,
            status: workspaceLifecycleStatus(project),
            updated: project.updated_at ? new Date(project.updated_at).toLocaleDateString('en-GB') : '',
          };
        })
      );
      downloadXls(`workspaces-${new Date().toISOString().slice(0, 10)}.xls`, xls);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to export workspaces');
    } finally {
      setExporting(false);
    }
  }, [isList, listMatching, ops, view]);

  const pagination = total > 0 && (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'center', sm: 'center' }}
      gap={1}
      sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
    >
      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
        Showing {showingFrom}–{showingTo} of {total}
      </Typography>
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" justifyContent="center">
        <Select
          size="sm"
          value={String(pageSize)}
          onChange={(_, value) => {
            setPageSize(Number(value) || DEFAULT_WORKSPACE_PAGE_SIZE);
            setPage(1);
          }}
          sx={{ minWidth: 88 }}
        >
          {WORKSPACE_PAGE_SIZE_OPTIONS.map((size) => (
            <Option key={size} value={String(size)}>{size} / page</Option>
          ))}
        </Select>
        <IconButton
          size="sm"
          variant="plain"
          disabled={page <= 1 || loading}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          aria-label="Previous page"
        >
          <IconWrapper icon={FaChevronLeft} />
        </IconButton>
        <Typography level="body-sm">
          Page {page} of {totalPages}
        </Typography>
        <IconButton
          size="sm"
          variant="plain"
          disabled={page >= totalPages || loading}
          onClick={() => setPage((current) => current + 1)}
          aria-label="Next page"
        >
          <IconWrapper icon={FaChevronRight} />
        </IconButton>
      </Stack>
    </Stack>
  );

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, pb: 4, width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography level="h2">{isList ? 'Workspace list' : 'Workspaces'}</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            {isList ? 'Search, filter, and open any workspace.' : 'What is open now.'}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startDecorator={<IconWrapper icon={FaDownload} />}
            loading={exporting}
            disabled={isList ? (loading || total === 0) : (opsLoading && (view === 'live' || view === 'presale'))}
            onClick={() => void exportCurrentView()}
          >
            Export
          </Button>
          <Button startDecorator={<IconWrapper icon={FaPlus} />} onClick={openCreate}>
            New workspace
          </Button>
        </Stack>
      </Stack>

      {(error || actionError || (!isList && opsError)) && (
        <Alert color="danger" sx={{ mb: 2 }}>{actionError || (!isList && opsError) || error}</Alert>
      )}

      {!isList && (
      <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mb: 2 }}>
        {DASHBOARD_VIEWS.map((item) => (
          <Chip
            key={item.id}
            size="sm"
            variant={view === item.id ? 'solid' : 'outlined'}
            onClick={() => setView(item.id)}
          >
            {item.label}
          </Chip>
        ))}
      </Stack>
      )}

      {!isList && (
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} sx={{ mb: 2 }}>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Typography level="h3">{opsLoading ? '—' : ops.needsOffice}</Typography>
              <Typography level="body-sm">Needs office now</Typography>
              {!opsLoading && ops.exceptionCount > 0 && (
                <Typography level="body-xs" color="danger" fontWeight="lg">
                  {ops.exceptionCount} booking exception{ops.exceptionCount === 1 ? '' : 's'}
                </Typography>
              )}
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Typography level="h3">{opsLoading ? '—' : ops.waitingCustomer}</Typography>
              <Typography level="body-sm">Waiting on customer</Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Typography level="h3">{opsLoading ? '—' : ops.installsThisWeek}</Typography>
              <Typography level="body-sm">Installs this week</Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Typography level="h3">
                {opsLoading
                  ? '—'
                  : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(ops.issuedUnpaidTotal)}
              </Typography>
              <Typography level="body-sm">Issued, not paid</Typography>
            </CardContent>
          </Card>
        </Stack>
      )}

      {!isList && view === 'today' && !opsLoading && (
        <>
          <InstallsAtRiskCard rows={ops.installsAtRisk} />
          <PendingClosureCard rows={ops.pendingClosure} />
          <ReadyToBookCard rows={ops.readyToBook} />
        </>
      )}

      {!isList && view === 'today' && (
        opsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <WorkspaceDashboardToday ops={ops} filter={opsFilter} onFilter={setOpsFilter} />
        )
      )}
      {!isList && view === 'board' && (
        opsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <WorkspaceDashboardBoard ops={ops} />
        )
      )}
      {!isList && (view === 'live' || view === 'presale') && (
        opsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <WorkspaceDashboardJobs ops={ops} accepted={view === 'live'} />
        )
      )}
      {!isList && view === 'week' && (
        opsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <WorkspaceDashboardWeek ops={ops} />
        )
      )}
      {!isList && view === 'scheduled' && (
        opsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <WorkspaceDashboardScheduled ops={ops} />
        )
      )}
      {!isList && view === 'unscheduled' && (
        opsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <WorkspaceDashboardUnscheduled ops={ops} />
        )
      )}

      {isList && (
      <>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={1} sx={{ mb: 2 }}>
        <Input
          placeholder="Search name, customer, notes"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ flex: 1 }}
        />
        <Select
          value={statusFilter}
          onChange={(_, value) => {
            setStatusFilter((value as WorkspaceStatusFilter) || 'all');
            setPage(1);
          }}
          sx={{ minWidth: 160 }}
        >
          <Option value="all">All statuses</Option>
          <Option value="active">Active</Option>
          <Option value="completed">Completed</Option>
          <Option value="cancelled">Cancelled</Option>
        </Select>
        <Select
          value={stageFilter}
          onChange={(_, value) => {
            setStageFilter((value as typeof stageFilter) || 'all');
            setPage(1);
          }}
          sx={{ minWidth: 200 }}
        >
          <Option value="all">All stages</Option>
          {WORKSPACE_STAGE_FILTERS.map((stage) => (
            <Option key={stage} value={stage}>
              {workspaceStageLabel(stage)}
            </Option>
          ))}
        </Select>
      </Stack>

      {loading && projects.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && projects.length === 0 && (
        <Alert color="neutral">No workspaces match.</Alert>
      )}

      {projects.length > 0 && (
        <Stack gap={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
          {projects.map((project) => {
            return (
              <Card key={project.id} variant="outlined">
                <CardContent>
                  <Typography level="title-md">{project.project_name || 'Untitled'}</Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    {project.customer_name || 'No customer'}
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1} flexWrap="wrap">
                    <LifecycleRefLink projectId={project.id} workspaceRef={project.lifecycle_ref} refs={lifecycleRefs} />
                    <Typography level="title-sm">
                      {formatProjectTotal(projectTotals, project.id)}
                      <Typography level="body-xs" sx={{ color: 'neutral.500', ml: 0.5 }}>inc VAT</Typography>
                    </Typography>
                  </Stack>
                  <Stack direction="row" gap={1} sx={{ my: 1 }} flexWrap="wrap" alignItems="flex-start">
                    <StageCell snapshot={snapshotFor(project)} />
                    {project.supply_only && <Chip size="sm" variant="outlined">Supply only</Chip>}
                  </Stack>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Button size="sm" component={Link} to={`/projects/${project.id}/workspace`}>
                      Open
                    </Button>
                    <Button size="sm" variant="outlined" onClick={() => openEdit(project)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="outlined" color="danger" onClick={() => setDeleteTarget(project)}>
                      Delete
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {projects.length > 0 && (
        <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
        <Table
          stripe="even"
          hoverRow
          sx={{
            minWidth: 860,
            '& th:last-child, & td:last-child': { textAlign: 'right' },
            '& th, & td': { verticalAlign: 'middle' },
            '& .col-updated': { display: { md: 'none', lg: 'table-cell' } },
            '& .col-total': { textAlign: 'right', whiteSpace: 'nowrap' },
          }}
        >
          <thead>
            <tr>
              <th style={{ width: '24%' }}>Workspace</th>
              <th style={{ width: '20%' }}>Customer</th>
              <th style={{ width: 190 }}>Stage</th>
              <th style={{ width: 90 }}>Ref</th>
              <th className="col-total" style={{ width: 125 }}>Total inc VAT</th>
              <th className="col-updated" style={{ width: 100 }}>Updated</th>
              <th style={{ width: 230 }} />
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => {
              return (
                <tr key={project.id}>
                  <td>
                    <Typography
                      level="body-sm"
                      fontWeight="lg"
                      component={Link}
                      to={`/projects/${project.id}/workspace`}
                      sx={{ textDecoration: 'none' }}
                    >
                      {project.project_name || 'Untitled'}
                    </Typography>
                    {project.supply_only && (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Supply only</Typography>
                    )}
                  </td>
                  <td>
                    <Typography level="body-sm">{project.customer_name || '—'}</Typography>
                    <Typography level="body-xs" noWrap title={project.customer_email || undefined} sx={{ color: 'neutral.500' }}>
                      {project.customer_email || ''}
                    </Typography>
                  </td>
                  <td>
                    <StageCell snapshot={snapshotFor(project)} />
                  </td>
                  <td>
                    <LifecycleRefLink projectId={project.id} workspaceRef={project.lifecycle_ref} refs={lifecycleRefs} />
                  </td>
                  <td className="col-total">
                    <Typography level="body-sm" fontWeight="md">{formatProjectTotal(projectTotals, project.id)}</Typography>
                  </td>
                  <td className="col-updated">
                    <Typography level="body-sm">
                      {project.updated_at ? new Date(project.updated_at).toLocaleDateString('en-GB') : '—'}
                    </Typography>
                  </td>
                  <td>
                    <Stack direction="row" gap={0.5} justifyContent="flex-end">
                      <Button size="sm" variant="plain" component={Link} to={`/projects/${project.id}/workspace`}>
                        Open
                      </Button>
                      <Button size="sm" variant="plain" onClick={() => openEdit(project)} startDecorator={<IconWrapper icon={FaEdit} />}>
                        Edit
                      </Button>
                      <Button size="sm" variant="plain" color="danger" onClick={() => setDeleteTarget(project)} startDecorator={<IconWrapper icon={FaTrash} />}>
                        Delete
                      </Button>
                    </Stack>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        </Box>
      )}

      {pagination}
      </>
      )}

      <Modal
        open={editorOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        }}
      >
        <ModalDialog
          variant="outlined"
          sx={{
            width: 440,
            maxWidth: 'calc(100vw - 24px)',
            p: 2,
            gap: 1,
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {addingCustomer && (
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => setAddingCustomer(false)}
                aria-label="Back to workspace"
              >
                <IconWrapper icon={FaArrowLeft} />
              </IconButton>
            )}
            {addingCustomer
              ? 'New customer'
              : editorMode === 'create'
                ? 'New workspace'
                : 'Edit workspace'}
          </DialogTitle>
          {!addingCustomer && (
            <Typography level="body-sm" sx={{ color: 'neutral.500', mt: -0.5 }}>
              {editorMode === 'create'
                ? 'Name the job and attach a customer.'
                : 'Update the name, customer, or status.'}
            </Typography>
          )}
          <DialogContent sx={{ overflow: 'visible', p: 0, mx: 0 }}>
            {addingCustomer ? (
              <Stack gap={1.25}>
                <CustomerCreationForm
                  compact
                  onCustomerCreated={(created) => {
                    addCustomer(created);
                    applyCustomer(created);
                    setAddingCustomer(false);
                  }}
                  onCancel={() => setAddingCustomer(false)}
                />
                <DialogActions sx={{ px: 0, pt: 0, pb: 0, flexDirection: 'row', justifyContent: 'flex-end' }}>
                  <Button variant="plain" color="neutral" onClick={discardEditor}>
                    Cancel
                  </Button>
                </DialogActions>
              </Stack>
            ) : (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void persistWorkspace();
                }}
              >
                <Stack gap={1.25}>
                  {formError && <Alert color="danger" size="sm">{formError}</Alert>}
                  <FormControl required size="sm">
                    <FormLabel>Workspace name</FormLabel>
                    <Input
                      autoFocus
                      value={form.project_name}
                      onChange={(event) => setForm((prev) => ({ ...prev, project_name: event.target.value }))}
                      placeholder="Smith Residence"
                    />
                  </FormControl>
                  <FormControl required size="sm">
                    <FormLabel>Customer</FormLabel>
                    <Stack direction="row" gap={0.75} alignItems="center">
                      <Autocomplete<Customer, false, false, false>
                        size="sm"
                        placeholder="Search directory"
                        options={customers}
                        loading={customersLoading}
                        value={form.customer}
                        onChange={(_, value) => applyCustomer(value)}
                        getOptionLabel={(option) =>
                          typeof option === 'string' ? option : option.name
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        sx={{ flex: 1, minWidth: 0 }}
                        filterOptions={(options, state) => {
                          const needle = state.inputValue.trim().toLowerCase();
                          if (!needle) return options;
                          return options.filter((option) =>
                            [option.name, option.email, option.phone]
                              .filter(Boolean)
                              .some((value) => String(value).toLowerCase().includes(needle))
                          );
                        }}
                        slotProps={{
                          listbox: { sx: { maxHeight: 280 } },
                          input: {
                            onKeyDown: (event) => {
                              if (event.key === 'Enter') event.stopPropagation();
                            },
                          },
                        }}
                        renderOption={(props, option) => (
                          <AutocompleteOption {...props}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography level="body-sm">{option.name}</Typography>
                              {option.email && (
                                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                                  {option.email}
                                </Typography>
                              )}
                            </Box>
                          </AutocompleteOption>
                        )}
                      />
                      <Tooltip title="Add a customer" placement="top">
                        <IconButton
                          size="sm"
                          variant="outlined"
                          color="neutral"
                          onClick={() => setAddingCustomer(true)}
                          aria-label="Add a customer"
                        >
                          <IconWrapper icon={FaUserPlus} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {form.customer?.email && (
                      <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                        {form.customer.email}
                      </Typography>
                    )}
                  </FormControl>
                  {editorMode === 'create' && brands.length > 0 && (
                    <FormControl size="sm">
                      <FormLabel>Brand</FormLabel>
                      <Select
                        size="sm"
                        value={chosenBrandId || null}
                        onChange={(_, value) => setForm((prev) => ({ ...prev, organisation_id: String(value || '') }))}
                      >
                        {selectableBrands(brands).map((brand) => (
                          <Option key={brand.id} value={brand.id}>{brand.tradingName} ({brand.code})</Option>
                        ))}
                      </Select>
                      <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                        {form.organisation_id
                          ? 'Quotes, invoices, numbering and emails for this workspace use this brand.'
                          : `From the customer${suggestedBrand ? `: ${suggestedBrand.tradingName}` : ''}. Quotes, invoices, numbering and emails follow it.`}
                      </Typography>
                    </FormControl>
                  )}
                  <FormControl size="sm">
                    <FormLabel>Notes</FormLabel>
                    <Textarea
                      minRows={2}
                      maxRows={4}
                      value={form.notes}
                      onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                      placeholder="Optional"
                    />
                  </FormControl>
                  <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                    <Checkbox
                      size="sm"
                      label="Supply only"
                      checked={form.supply_only}
                      onChange={(event) => setForm((prev) => ({ ...prev, supply_only: event.target.checked }))}
                    />
                    {editorMode === 'edit' && (
                      <Select
                        size="sm"
                        value={form.project_status}
                        onChange={(_, value) =>
                          setForm((prev) => ({
                            ...prev,
                            project_status: (value as 'active' | 'completed' | 'cancelled') || 'active',
                          }))
                        }
                        sx={{ minWidth: 140, ml: 'auto' }}
                      >
                        <Option value="active">Active</Option>
                        <Option value="completed">Completed</Option>
                        <Option value="cancelled">Cancelled</Option>
                      </Select>
                    )}
                  </Stack>
                  <DialogActions sx={{ px: 0, pt: 0.5, pb: 0, flexDirection: 'row', justifyContent: 'flex-end', gap: 1 }}>
                    <Button variant="plain" color="neutral" onClick={discardEditor}>
                      Cancel
                    </Button>
                    <Button type="submit" loading={saving}>
                      {editorMode === 'create' ? 'Create and open' : 'Save'}
                    </Button>
                  </DialogActions>
                </Stack>
              </form>
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>

      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <ModalDialog>
          <DialogTitle>Delete workspace</DialogTitle>
          <DialogContent>
            <Typography>
              Delete {deleteTarget?.project_name || 'this workspace'}? This removes the entire job: First and Final Surveys, quote versions, customer transmissions and share links, deltas, activity history, and associated workspace records. Nothing from this lifecycle is left behind. Classic Projects uses the same table, so it will disappear there too.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="danger" loading={deleting} onClick={confirmDelete}>Delete</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default WorkspacesDashboardPage;
